import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSido, getSigungu, getLastSync,
  querySupply, queryPlanned, addManualPlanned, uploadCsv,
  loadSync, startSync, stopSync, getSyncState,
} from '../../lib/api/admin-supply-api';
import './SupplyManagement.css';

const DATA_GO_URL = 'https://www.data.go.kr/data/15111714/fileData.do';
const BIZ_TYPES = ['분양', '조합', '임대', '분양임대조합', '분양임대', '분양조합'];

const fmtNum = (v) => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};
const sum = (rows, f) => (rows || []).reduce((a, r) => a + (Number(r?.[f]) || 0), 0);
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const yearAgoStr = () => {
  const d = new Date();
  const p = new Date(d.getFullYear() - 1, d.getMonth(), 1);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`;
};

function SupplyManagement() {
  const { token } = useAuth();
  const [sidoList, setSidoList] = useState([]);
  const [error, setError] = useState('');

  const [topTab, setTopTab] = useState('query');       // query | sync
  const [querySub, setQuerySub] = useState('supply');  // supply | planned
  const [syncSub, setSyncSub] = useState('supply');    // supply | planned

  useEffect(() => {
    (async () => {
      try { setSidoList(await getSido(token)); }
      catch (e) { setError(e?.message || '시도 조회 실패'); }
    })();
  }, [token]);

  return (
    <div className="supply-management">
      {!!error && <div className="admin-table-status error">{error}</div>}

      <div className="supply-tabs">
        <button className={`supply-tab ${topTab === 'query' ? 'active' : ''}`} onClick={() => setTopTab('query')}>조회</button>
        <button className={`supply-tab ${topTab === 'sync' ? 'active' : ''}`} onClick={() => setTopTab('sync')}>동기화</button>
      </div>

      {topTab === 'query' && (
        <>
          <div className="supply-subtabs">
            <button className={`supply-subtab ${querySub === 'supply' ? 'active' : ''}`} onClick={() => setQuerySub('supply')}>분양/입주 물량</button>
            <button className={`supply-subtab ${querySub === 'planned' ? 'active' : ''}`} onClick={() => setQuerySub('planned')}>분양 예정</button>
          </div>
          {querySub === 'supply' ? <SupplyQueryPane token={token} sidoList={sidoList} setError={setError} />
            : <PlannedPane token={token} sidoList={sidoList} setError={setError} withManage={false} />}
        </>
      )}

      {topTab === 'sync' && (
        <>
          <div className="supply-subtabs">
            <button className={`supply-subtab ${syncSub === 'supply' ? 'active' : ''}`} onClick={() => setSyncSub('supply')}>분양/입주 물량 동기화</button>
            <button className={`supply-subtab ${syncSub === 'planned' ? 'active' : ''}`} onClick={() => setSyncSub('planned')}>분양예정 동기화</button>
          </div>
          {syncSub === 'supply' ? <SyncPane token={token} sidoList={sidoList} setError={setError} active={topTab === 'sync' && syncSub === 'supply'} />
            : <PlannedPane token={token} sidoList={sidoList} setError={setError} withManage />}
        </>
      )}
    </div>
  );
}

// 공통 지역 셀렉터 훅
function useRegion(token, setError) {
  const [sigunguList, setSigunguList] = useState([]);
  const [sido, setSido] = useState('');
  const [sidoLabel, setSidoLabel] = useState('');
  const [sigungu, setSigungu] = useState('');

  const onSidoChange = useCallback(async (code, label) => {
    setSido(code); setSidoLabel(label); setSigungu(''); setSigunguList([]);
    if (!code) return;
    try { setSigunguList(await getSigungu(token, code)); }
    catch (e) { setError(e?.message || '시군구 조회 실패'); }
  }, [token, setError]);

  return { sigunguList, sido, sidoLabel, sigungu, setSigungu, onSidoChange };
}

// ===== 조회: 분양/입주 물량 (g1) =====
function SupplyQueryPane({ token, sidoList, setError }) {
  const { sigunguList, sido, sidoLabel, sigungu, setSigungu, onSidoChange } = useRegion(token, setError);
  const [from, setFrom] = useState(yearAgoStr());
  const [to, setTo] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState({});

  useEffect(() => {
    (async () => { try { setLastSync(await getLastSync(token)); } catch { /* noop */ } })();
  }, [token]);

  const doQuery = useCallback(async () => {
    setLoading(true);
    try {
      const r = await querySupply(token, { sido: sidoLabel || '전체', sigungu, start: from, end: to });
      if (r.ok === false) { setError(r.message || '조회 실패'); setRows([]); return; }
      const merged = [
        ...(r.presale || []).map((x) => ({ kind: '분양', ...x })),
        ...(r.movein || []).map((x) => ({ kind: '입주', ...x })),
      ];
      setRows(merged);
    } catch (e) { setError(e?.message || '조회 실패'); setRows([]); }
    finally { setLoading(false); }
  }, [token, sidoLabel, sigungu, from, to, setError]);

  return (
    <div className="supply-pane">
      <div className="supply-bar">
        <label>시작일</label>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <label>종료일</label>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <RegionSelects sidoList={sidoList} sigunguList={sigunguList} sido={sido} sigungu={sigungu}
          onSido={onSidoChange} onSigungu={setSigungu} />
        <button className="supply-btn primary" onClick={doQuery}>조회</button>
      </div>
      <div className="supply-lastsync">
        마지막 동기화 일자 — 분양: {lastSync.presaleBaseDt || '-'} / 입주: {lastSync.moveinBaseDt || '-'}
      </div>
      {loading && <div className="admin-table-status">조회 중...</div>}
      <div className="supply-table-wrap">
        <table className="admin-basic-table supply-table">
          <thead>
            <tr>
              <th>구분</th><th>시/도</th><th>시/군/구</th><th>기준월</th>
              <th className="num">단지수</th><th className="num">세대수</th>
              <th className="num">일반</th><th className="num">조합</th><th className="num">임대</th><th className="num">기타</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.kind}</td><td>{r.sidoName || '-'}</td><td>{r.sigunguName || '-'}</td><td>{r.baseDt || '-'}</td>
                <td className="num">{fmtNum(r.complexCount)}</td>
                <td className="num">{r.kind === '입주' ? fmtNum(r.householdCount) : '-'}</td>
                <td className="num">{r.kind === '분양' ? fmtNum(r.general) : '-'}</td>
                <td className="num">{r.kind === '분양' ? fmtNum(r.union) : '-'}</td>
                <td className="num">{r.kind === '분양' ? fmtNum(r.rental) : '-'}</td>
                <td className="num">{r.kind === '분양' ? fmtNum(r.other) : '-'}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && <tr><td colSpan={10} className="muted center">조회 결과가 없습니다.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="supply-total">
                <td>합계</td><td /><td /><td />
                <td className="num">{fmtNum(sum(rows, 'complexCount'))}</td>
                <td className="num">{fmtNum(sum(rows.filter((r) => r.kind === '입주'), 'householdCount'))}</td>
                <td className="num">{fmtNum(sum(rows, 'general'))}</td>
                <td className="num">{fmtNum(sum(rows, 'union'))}</td>
                <td className="num">{fmtNum(sum(rows, 'rental'))}</td>
                <td className="num">{fmtNum(sum(rows, 'other'))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ===== 분양 예정 (g4·g5) =====
function PlannedPane({ token, sidoList, setError, withManage }) {
  const { sigunguList, sido, sigungu, setSigungu, onSidoChange } = useRegion(token, setError);
  const [sort, setSort] = useState('desc');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const fileRef = useRef(null);

  const doQuery = useCallback(async (allowAll = false) => {
    setLoading(true);
    try {
      const r = await queryPlanned(token, { sido, sigungu, sort, all: allowAll });
      if (r.needConfirmAll) {
        setLoading(false);
        if (window.confirm('지역을 선택하지 않았습니다. 전체(최대 5000건)를 조회할까요?')) {
          return doQuery(true);
        }
        return;
      }
      setRows(r.rows || []);
    } catch (e) { setError(e?.message || '조회 실패'); setRows([]); }
    finally { setLoading(false); }
  }, [token, sido, sigungu, sort, setError]);

  const onUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true);
    try {
      const { ok, message, result } = await uploadCsv(token, file);
      if (!ok) { alert(message || '업로드 실패'); return; }
      alert(`업로드 완료 — 총 ${result?.total || 0} / 저장 ${result?.saved || 0} / DB중복 ${result?.dbDuplicates || 0} / 실패 ${result?.failedCount || 0}`);
      doQuery(!sido && !sigungu);
    } catch (err) { alert(err?.message || '업로드 실패'); }
    finally { setLoading(false); }
  }, [token, sido, sigungu, doQuery]);

  return (
    <div className="supply-pane">
      <div className="supply-bar">
        <RegionSelects sidoList={sidoList} sigunguList={sigunguList} sido={sido} sigungu={sigungu}
          onSido={onSidoChange} onSigungu={setSigungu} />
        <label>정렬</label>
        <select value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="desc">입주월 내림차순</option>
          <option value="asc">입주월 오름차순</option>
        </select>
        <button className="supply-btn primary" onClick={() => doQuery(false)}>조회</button>
        {withManage && (
          <>
            <button className="supply-btn" onClick={() => fileRef.current?.click()}>CSV업로드</button>
            <button className="supply-btn" onClick={() => setModalOpen(true)}>수동추가</button>
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onUpload} />
          </>
        )}
        <button className="supply-btn" onClick={() => window.open(DATA_GO_URL, '_blank')}>데이터 다운로드</button>
      </div>
      {loading && <div className="admin-table-status">처리 중...</div>}
      <div className="supply-table-wrap">
        <table className="admin-basic-table supply-table">
          <thead>
            <tr>
              <th>입주예정월</th><th>지역</th><th>사업유형</th><th>주소</th><th>아파트명</th><th className="num">세대수</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.moveinExpectedMonth || '-'}</td><td>{r.regionName || '-'}</td><td>{r.businessType || '-'}</td>
                <td>{r.address || '-'}</td><td>{r.apartmentName || '-'}</td><td className="num">{fmtNum(r.householdCount)}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && <tr><td colSpan={6} className="muted center">조회 결과가 없습니다.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="supply-total">
                <td>합계</td><td /><td /><td /><td /><td className="num">{fmtNum(sum(rows, 'householdCount'))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {modalOpen && <ManualAddModal token={token} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); doQuery(!sido && !sigungu); }} />}
    </div>
  );
}

function ManualAddModal({ token, onClose, onSaved }) {
  const [form, setForm] = useState({
    bjdCode: '', moveinExpectedMonth: '', regionName: '', businessType: '분양', address: '', apartmentName: '', householdCount: '0',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const { ok, message } = await addManualPlanned(token, form);
    if (!ok) { alert(message || '저장 실패'); return; }
    onSaved();
  };
  return (
    <div className="supply-modal-bg" onClick={onClose}>
      <div className="supply-modal" onClick={(e) => e.stopPropagation()}>
        <div className="supply-modal-title">분양예정데이터 수동추가</div>
        {[
          ['bjdCode', 'bjd_code'], ['moveinExpectedMonth', '입주예정월'], ['regionName', '지역'],
        ].map(([k, label]) => (
          <div className="supply-modal-row" key={k}><label>{label}</label>
            <input value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
        ))}
        <div className="supply-modal-row"><label>사업유형</label>
          <select value={form.businessType} onChange={(e) => set('businessType', e.target.value)}>
            {BIZ_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select></div>
        {[['address', '주소'], ['apartmentName', '아파트명'], ['householdCount', '세대수']].map(([k, label]) => (
          <div className="supply-modal-row" key={k}><label>{label}</label>
            <input value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
        ))}
        <div className="supply-modal-actions">
          <button className="supply-btn" onClick={onClose}>취소</button>
          <button className="supply-btn primary" onClick={save}>저장</button>
        </div>
      </div>
    </div>
  );
}

// ===== 동기화: 분양/입주 물량 (g3) =====
function SyncPane({ token, sidoList, setError, active }) {
  const [sido, setSido] = useState('');
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [state, setState] = useState({ running: false, status: '준비 중', progressText: '' });
  const pollRef = useRef(null);

  const refreshState = useCallback(async () => {
    try {
      const s = await getSyncState(token);
      setState({ running: s.running, status: s.status, progressText: s.progressText });
      if (s.rows?.length) setRows(s.rows);
      setLogs(s.logs || []);
      return s.running;
    } catch { return false; }
  }, [token]);

  // 1.2초 폴링 (해당 서브탭 활성 & 수집 중일 때)
  useEffect(() => {
    if (!active) return undefined;
    refreshState();
    pollRef.current = setInterval(async () => {
      const running = await refreshState();
      if (!running && pollRef.current) { /* keep light poll to catch final state */ }
    }, 1200);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active, refreshState]);

  const doLoad = async () => {
    const { ok, message, rows: r } = await loadSync(token, sido);
    if (!ok) { setError(message || '시군구 로드 실패'); return; }
    setRows(r);
  };
  const doStart = async () => {
    const { ok, message } = await startSync(token, { timeout: 20, delay: 0.3 });
    if (!ok) { alert(message || '시작 실패'); return; }
    refreshState();
  };
  const doStop = async () => {
    const { ok, message } = await stopSync(token);
    if (!ok) { alert(message || '중지 실패'); }
  };

  return (
    <div className="supply-pane">
      <div className="supply-bar">
        <label>시도</label>
        <select value={sido} onChange={(e) => setSido(e.target.value)}>
          <option value="">전체</option>
          {sidoList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <button className="supply-btn" onClick={doLoad}>시군구 불러오기</button>
        <button className="supply-btn primary" onClick={doStart} disabled={state.running}>전체 수집 시작</button>
        <button className="supply-btn danger" onClick={doStop} disabled={!state.running}>중지</button>
      </div>
      <div className="supply-progress">
        {state.progressText || state.status} {state.running ? '· 수집 중...' : ''}
      </div>
      <div className="supply-table-wrap sync">
        <table className="admin-basic-table supply-table">
          <thead>
            <tr>
              <th>시도</th><th>시군구</th><th>법정동코드</th>
              <th className="num">분양</th><th className="num">입주</th><th>상태</th><th>처리시각</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.bjdCode}>
                <td>{r.sido || '-'}</td><td>{r.sigungu || '-'}</td><td className="muted">{r.bjdCode}</td>
                <td className="num">{r.presale}</td><td className="num">{r.movein}</td>
                <td className={r.status?.startsWith('오류') ? 'err' : ''}>{r.status}</td><td>{r.updatedAt || '-'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="muted center">시군구 불러오기를 눌러 대상을 로드하세요.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="supply-log">
        <div className="supply-log-title">로그</div>
        <textarea readOnly value={(logs || []).join('\n')} />
      </div>
    </div>
  );
}

function RegionSelects({ sidoList, sigunguList, sido, sigungu, onSido, onSigungu }) {
  return (
    <>
      <label>시도</label>
      <select value={sido} onChange={(e) => onSido(e.target.value, e.target.selectedOptions[0]?.dataset.label || '')}>
        <option value="" data-label="전체">전체</option>
        {sidoList.map((o) => <option key={o.code} value={o.code} data-label={o.label}>{o.label}</option>)}
      </select>
      <label>시군구</label>
      <select value={sigungu} onChange={(e) => onSigungu(e.target.value)} disabled={!sido}>
        <option value="">전체</option>
        {sigunguList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
    </>
  );
}

export default SupplyManagement;
